import { Component, ElementRef, Input, OnInit, ViewChild, ViewChildren, QueryList, inject, Renderer2, viewChild, OnDestroy } from '@angular/core';
import { ButtonCloseDirective, ModalBodyComponent, ModalComponent, ModalFooterComponent, ModalHeaderComponent, ModalTitleDirective, OffcanvasComponent, OffcanvasService, ThemeDirective } from '@coreui/angular';
import { OffcanvasLevelCheckPipe } from './offcanvas-level-check.pipe';
import { OffCanvasPlacement, OptimaOffCanvasService,OptimaOffCanvasComponent, closeOffCanvas } from '@optima-stride-ai/optima-components';
import { FormSelectorPipe } from './form-selector.pipe';
import { AppStateService } from '../../state/app-state';
import { notifyStrategyEntityCreated } from '../../strategy/strategy-explorer/strategy-explorer-creation';
import { FormStates, OffCanvasComponents, OffCanvasRightContentTypes } from '../off-canvas-service/off-canvas-form.service';
import { FormObjectiveComponent } from '../../objective/form-objective/form-objective.component';
import  {FormDeliverableComponent } from '../../deliverables/form-deliverable/form-deliverable.component';
import {FormKPIComponent} from '../../kpi/form-kpi/form-kpi.component';
import { FormMeasureComponent } from '../../transaction(Measure)/form-measure/form-measure.component';
import { FormTransactionLogComponent } from '../../transaction-log/form-transaction-log/form-transaction-log.component';
import { FormAddDashboardComponent } from "../../dashboard/form-add-dashboard/form-add-dashboard.component";
import { FormBizEntityComponent, FormBizEntityTypeComponent, NotificationComponent } from "@optima-stride-ai/optima-app-framework";
import { IBizEntity, IBizEntityType } from '@optima-stride-ai/core';
import { Subscription } from 'rxjs';
import { getEmptyBizEntity } from '../../utils/form/function-init-form-state';
import { FormSectionComponent } from '@optima-stride-ai/optima-app-framework';


@Component({
    selector: 'app-off-canvas-form',
    imports: [
    OffcanvasLevelCheckPipe,
    ButtonCloseDirective,
    ThemeDirective,
    FormSelectorPipe,
    FormObjectiveComponent,
    FormDeliverableComponent,
    FormKPIComponent,
    FormMeasureComponent,
    FormTransactionLogComponent,
    FormAddDashboardComponent,
    OptimaOffCanvasComponent,
    FormBizEntityComponent,
    FormBizEntityTypeComponent,
    NotificationComponent,
    FormSectionComponent
],
    templateUrl: './off-canvas-form.component.html',
    styleUrl: './off-canvas-form.component.sass'
})
export class OffCanvasComponentSelector implements OnInit, OnDestroy {
  @Input() id: string = "";
  @Input() placement:OffCanvasPlacement = OffCanvasPlacement.End;
  @Input() level: number = 1;
  @ViewChild(OffcanvasComponent) offCanvas!: ElementRef;
  bizEntity?: IBizEntity;
  bizEntityTypeForEdit?: IBizEntityType;
  parentForSection?: IBizEntity;
  isEditing: boolean = false;
  private offCanvasServiceRef = inject(OffcanvasService);
  private renderStateSubscription?: Subscription;
  /** Tracks changes to generic forms rendered by either off-canvas branch. */
  private genericFormsChangeSubscription?: Subscription;
  /** One completion subscription per rendered form, retained across query changes. */
  private readonly genericFormSaveSubscriptions = new Map<FormBizEntityComponent, Subscription>();
  /**
   * Connects the framework's saveComplete output without changing the template.
   * Both enum-based and component-based generic forms use this submission path.
   */
  @ViewChildren(FormBizEntityComponent)
  set genericForms(forms: QueryList<FormBizEntityComponent>) {
    this.genericFormsChangeSubscription?.unsubscribe();
    this.subscribeToGenericFormSaves(forms);
    this.genericFormsChangeSubscription = forms.changes.subscribe(() => {
      this.subscribeToGenericFormSaves(forms);
    });
  }
  offCanvasFormService = inject(OptimaOffCanvasService)
  private appStateService: AppStateService = inject(AppStateService);
  offCanvasComponents = OffCanvasComponents;
  LibFormBizEntityComponent = FormBizEntityComponent; // Need to assign to a variable to use in template
  LibFormBizEntityTypeComponent = FormBizEntityTypeComponent; // Need to assign to a variable to use in template
  LibNavigationComponent = NotificationComponent
  FormSectionComponent = FormSectionComponent;
  get childComp() { return OffCanvasComponents }

  offCanvasClass() {
    if (this.level == 1) return 'optima-off-canvas'
    else return `optima-off-canvas-l-${this.level} optima-off-leveled`;
  }

  renderErrors = false;
  constructor(private renderer: Renderer2) {
    this.renderStateSubscription = this.offCanvasServiceRef.offcanvasState$.subscribe((state) => {
      if (state.id !== this.id) return; // We have multiple levels and if we don't put this condition, other levels will also run show/hide methods.
      
      // Ignore spurious show:false events triggered by modal close destroying offcanvas components
      // CoreUI's OffcanvasComponent.ngOnDestroy() calls toggle({show: false}) which broadcasts to all subscribers
      // Check if offcanvas should still be open based on our own service's state
      
      if (!state.show && !this.offCanvasFormService.popstateInProgress && this.offCanvasFormService.currentHighestLevel >= this.level) {
        // console.log(`Ignoring spurious show:false for ${this.id} - offcanvas level ${this.level} should still be open (highest: ${this.offCanvasFormService.currentHighestLevel})`);
        // Re-emit show:true to counteract CoreUI's internal state change
        // Use setTimeout to avoid infinite loop and let current event cycle complete
        setTimeout(() => {
          this.offCanvasServiceRef.toggle({ id: this.id, show: true });
        }, 0);
        return;
      }
      
      if (state.show) {
        this.renderErrors = true; //if required render errors as form is visible and showing error makes sense
        this.patchFormValues();
        // This is important ot avoid stale bizEntity Type
        this.bizEntity = undefined;
        this.bizEntityTypeForEdit = undefined;
        this.parentForSection = undefined;
        this.isEditing = false;

        const data = this.offCanvasFormService.dataOfCurrentFormInView;
        
        // Priority 1: Check dataOfCurrentFormInView first, this is set by framework when opening off-canvas for editing aan entity type
        if (data) {
          if (data.bizEntity) {
            this.bizEntity = data.bizEntity;
            this.isEditing = data.isEditing || false;
          }
          if (data.bizEntityType) {
            this.bizEntityTypeForEdit = data.bizEntityType;
            this.isEditing = data.isEditing || false;
          }
          if (data.parent) {
            this.parentForSection = data.parent;
            this.isEditing = data.isEditing || false;
          }
        } 
        // Priority 2: Fall back to AppStateService only if no data from dataOfCurrentFormInView
        else if (this.appStateService.entityFormState === FormStates.New && this.appStateService.selectedBizEntityTypeForNewBizEntity) {
          this.bizEntity = getEmptyBizEntity(this.appStateService.selectedBizEntityTypeForNewBizEntity);
          // Patch parentId when creating from a context entity
          const contextEntity = this.appStateService.selectedBizEntityForEdit;
          if (contextEntity?.id) {
            this.bizEntity.parentId = contextEntity.id.toString();
          }
          this.isEditing = false;
        } else if (this.appStateService.entityFormState === FormStates.Edit && this.appStateService.selectedBizEntityForEdit) {
          this.bizEntity = { ...this.appStateService.selectedBizEntityForEdit };
          this.isEditing = true;
        }
      } else {
        this.renderErrors = false;  //don't render errors as form is not visible anyways. (if we show errors here, in some cases, errors will be shown for a fraction of second before form is closed)
        const currentOffCanvas = this.offCanvasFormService.getLastOffCanvasAtLevel(this.level);
        
        if (currentOffCanvas?.componentIsABizEntity) {
          // offcanvas is being closed and the child component is a biz entity component, let's clear the state in app state service.
          this.appStateService.entityFormState = FormStates.Idle;
          const comp = currentOffCanvas.componentInOffCanvas;
           //console.log(comp, OffCanvasComponents," is a biz entity component, so, on closing FormState is set to idle")
        } else if (this.appStateService.offCanvasRightContentType === OffCanvasRightContentTypes.Others) {
          this.appStateService.offCanvasRightContentType = OffCanvasRightContentTypes.None;
          this.appStateService.offCanvasRightComponent = OffCanvasComponents.None;
        }
        // This check in importnt as we are using both `dataOfCurrentFormInView` and `AppStateService` to manage state. 
        // Will need to refactor to use any one only
        this.offCanvasFormService.dataOfCurrentFormInView = null;
      }
    })
  }

  ngOnInit(): void {
  }

  private patchFormValues() {
    
  }

  ngOnDestroy(): void {
    this.renderStateSubscription?.unsubscribe();
    this.genericFormsChangeSubscription?.unsubscribe();
    this.genericFormSaveSubscriptions.forEach(subscription => subscription.unsubscribe());
    this.genericFormSaveSubscriptions.clear();
  }

  /**
   * Notifies once per successful generic-form creation, including relationship saves.
   * Uses the output's submitted entity (not wrapper context) so a changed or inferred
   * parentId is preserved. Updates and failures never publish creation notifications.
   *
   * @param forms - Currently rendered framework forms with typed saveComplete outputs.
   * @returns Nothing; maintains subscriptions until forms are removed or destroyed.
   */
  private subscribeToGenericFormSaves(forms: QueryList<FormBizEntityComponent>): void {
    const currentForms = new Set(forms.toArray());
    this.genericFormSaveSubscriptions.forEach((subscription, form) => {
      if (!currentForms.has(form)) {
        subscription.unsubscribe();
        this.genericFormSaveSubscriptions.delete(form);
      }
    });
    currentForms.forEach(form => {
      if (this.genericFormSaveSubscriptions.has(form)) {
        return;
      }
      let creationNotified = false;
      const subscription = form.saveComplete.subscribe(event => {
        if (creationNotified || form.isEditing || !event.success || event.operation !== 'create') {
          return;
        }
        creationNotified = true;
        notifyStrategyEntityCreated(event.bizEntity, event.response);
      });
      this.genericFormSaveSubscriptions.set(form, subscription);
    });
  }

  onCloseButtonClicked() {
    // this.offCanvasFormService.currentOffCanvas?.onClose!();
    closeOffCanvas(this.offCanvasFormService, this.offCanvasServiceRef, OffCanvasPlacement.End, true)
  }

  // get isMyLevel(): boolean {
  //   let vals = Object.values(OffCanvasIDsInDefaultLayout) as string[];
  //   let lvl = (vals).indexOf(this.id)
  //   return lvl == this.level - 1;
  // }
/**
   * Check if the current off-canvas should render a specific component
   */
  shouldRenderComponent(componentType: any): boolean {
    const currentOffCanvas = this.offCanvasFormService.getLastOffCanvasAtLevel(this.level);
    return currentOffCanvas?.componentInOffCanvas === componentType;
  }
}